/**
 * Shared cart + money helpers for bundle UIs (Build Your Routine, Custom Bundle Set, etc.).
 * Load after the Shopify global, before section controllers.
 */
(function (global) {
  'use strict';

  const getStoreRoot = () => {
    const r = global.Shopify?.routes?.root;
    if (r == null) return '/';
    return r.endsWith('/') ? r : `${r}/`;
  };

  const parentVariantGid = (numericId) =>
    `gid://shopify/ProductVariant/${Number(numericId)}`;

  const formatMoney = (cents) => {
    if (
      typeof global.FoxTheme !== 'undefined' &&
      global.FoxTheme.Currency?.formatMoney === 'function'
    ) {
      return global.FoxTheme.Currency.formatMoney(
        cents,
        global.FoxTheme.settings?.moneyFormat
      );
    }
    return (Number(cents) / 100).toFixed(2);
  };

  const fetchCart = () =>
    fetch(`${getStoreRoot()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then((res) => (res.ok ? res.json() : null));

  const productsJsonUrl = (handle) => {
    const base = getStoreRoot();
    return `${base}products/${encodeURIComponent(handle)}.js`;
  };

  const parseAddResponse = (res) =>
    res.text().then((bodyText) => {
      let data = null;
      try {
        data = bodyText ? JSON.parse(bodyText) : null;
      } catch {
        /* ignore */
      }
      if (!res.ok || (data?.status && Number(data.status) >= 400)) {
        let msg = data?.description || data?.message || '';
        if (!msg && bodyText) msg = bodyText.slice(0, 240);
        if (!msg) msg = 'Could not update cart.';
        throw new Error(msg);
      }
      return data;
    });

  const afterCartUpdate = () => {
    const isDrawer =
      global.FoxTheme?.settings?.cartType === 'drawer';
    if (isDrawer) {
      global.document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { open: true },
        })
      );
      return;
    }
    const cartBase = global.FoxTheme?.routes?.cart_url || '/cart';
    fetch(`${cartBase}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cartJson) => {
        if (cartJson) {
          global.FoxTheme?.pubsub?.publish(
            global.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            { cart: cartJson }
          );
        }
      })
      .catch(() => {});
  };

  const changeLineQuantityByKey = (lineKey, quantity) => {
    const addUrl = `${getStoreRoot()}cart/change.js`;
    return fetch(addUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: lineKey, quantity }),
    });
  };

  const getProp = (item, name) => {
    if (!item?.properties) return null;
    const v = item.properties[name];
    return v == null ? null : String(v);
  };

  const isBundleComponentForParent = (item, parentGid) => {
    if (!item || getProp(item, '_bundle_role') !== 'component') return false;
    return getProp(item, '_bundle_parent_variant_id') === parentGid;
  };

  /**
   * Tier reward lines (e.g. custom-bundle-set free gifts) carry
   * `_bundle_role: 'reward'` instead of `'component'`. Keeping them
   * out of `isBundleComponentForParent` is deliberate: the sidebar
   * line collector and component sweep should ignore rewards so
   * they do not get counted as components / mistaken for items the
   * customer picked manually.
   */
  const isBundleRewardForParent = (item, parentGid) => {
    if (!item || getProp(item, '_bundle_role') !== 'reward') return false;
    return getProp(item, '_bundle_parent_variant_id') === parentGid;
  };

  const isAnyBundleLineForParent = (item, parentGid) =>
    isBundleComponentForParent(item, parentGid) ||
    isBundleRewardForParent(item, parentGid);

  const gidToVariantNumeric = (gid) => {
    if (gid == null) return null;
    const s = String(gid).trim();
    const m = s.match(/ProductVariant\/(\d+)/i);
    if (m) return Number(m[1]);
    const n = /^\d+$/.test(s) ? Number(s) : NaN;
    return Number.isNaN(n) ? null : n;
  };

  /**
   * Parses the cart line property (JSON string of GIDs) into numeric variant ids, in order.
   */
  const parseBundleComponentVariantIds = (value) => {
    if (value == null) return [];
    const raw = String(value).trim();
    if (!raw) return [];
    let arr = null;
    try {
      arr = JSON.parse(raw);
    } catch {
      return [];
    }
    if (!Array.isArray(arr)) return [];
    return arr
      .map((g) => gidToVariantNumeric(g))
      .filter((id) => id != null);
  };

  const parseIntArrayFromJsonProperty = (value) => {
    if (value == null) return [];
    const raw = String(value).trim();
    if (!raw) return [];
    let parsed = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      const n = parseInt(raw, 10);
      return Number.isNaN(n) ? [] : [Math.max(1, n)];
    }
    if (Array.isArray(parsed)) {
      return parsed.map((x) => {
        const n = parseInt(String(x), 10);
        return Number.isNaN(n) ? 1 : Math.max(1, n);
      });
    }
    if (typeof parsed === 'number' && !Number.isNaN(parsed)) {
      return [Math.max(1, Math.floor(parsed))];
    }
    return [];
  };

  const hasBundledComponentMetadata = (item) =>
    Boolean(
      (item && getProp(item, '_bundle_component_variant_ids')) ||
        item?.has_components
    );

  /**
   * Collapsed "Bundle & Save" line: one parent variant with component GIDs in properties.
   * @param {object} [opts] - optional groupId: if the line has _bundle_group_id, it must match
   */
  const findBundleParentLine = (cart, parentVariantNumeric, opts) => {
    if (!cart?.items) return null;
    const n = Number(parentVariantNumeric);
    if (Number.isNaN(n)) return null;
    const wantGroup = opts?.groupId;
    for (const item of cart.items) {
      if (Number(item.variant_id) !== n) continue;
      if (!hasBundledComponentMetadata(item)) continue;
      if (wantGroup != null && String(wantGroup).length > 0) {
        const g = getProp(item, '_bundle_group_id');
        if (g != null && g !== '' && g !== wantGroup) continue;
      }
      return item;
    }
    return null;
  };

  /**
   * Removes the collapsed parent bundle line (if present) by line key. Call after clearAllBundleComponentsForParent.
   */
  const clearBundleParentLineIfPresent = (cart, parentVariantNumeric) => {
    const line = findBundleParentLine(cart, parentVariantNumeric);
    if (!line?.key) return Promise.resolve();
    return changeLineQuantityByKey(line.key, 0).then((res) => {
      if (!res.ok) {
        return Promise.reject(
          new Error('Could not remove the bundle line from the cart.')
        );
      }
    });
  };

  /**
   * Returns distinct bundle_group_id values in cart line order.
   */
  const findBundleGroupIdsForParent = (cart, parentGid) => {
    const out = [];
    if (!cart?.items) return out;
    const seen = new Set();
    for (const item of cart.items) {
      if (!isBundleComponentForParent(item, parentGid)) continue;
      const gid = getProp(item, '_bundle_group_id');
      if (!gid) continue;
      if (!seen.has(gid)) {
        seen.add(gid);
        out.push(gid);
      }
    }
    return out;
  };

  /**
   * First bundle group id in cart line order (canonical set).
   */
  const getCanonicalBundleGroupId = (cart, parentGid) => {
    if (!cart?.items) return null;
    for (const item of cart.items) {
      if (!isBundleComponentForParent(item, parentGid)) continue;
      return getProp(item, '_bundle_group_id') || null;
    }
    return null;
  };

  const collectLinesForGroup = (cart, parentGid, groupId) => {
    if (!cart?.items || !groupId) return [];
    return cart.items.filter(
      (item) =>
        isBundleComponentForParent(item, parentGid) &&
        getProp(item, '_bundle_group_id') === groupId
    );
  };

  /**
   * Removes lines that belong to a different group than the canonical first group
   * for this parent variant (sequential; line indices shift). Returns final cart.
   */
  const enforceSingleBundleGroup = (cart, parentGid) => {
    const gids = findBundleGroupIdsForParent(cart, parentGid);
    if (gids.length <= 1) {
      return Promise.resolve(cart);
    }
    const keep = gids[0];
    const toRemove = cart.items
      .filter(
        (item) =>
          isBundleComponentForParent(item, parentGid) &&
          getProp(item, '_bundle_group_id') &&
          getProp(item, '_bundle_group_id') !== keep
      )
      .map((item) => item.key)
      .filter(Boolean);
    if (!toRemove.length) return Promise.resolve(cart);
    return removeLineKeysSequentially(toRemove).then(() => fetchCart());
  };

  const removeLineKeysSequentially = (keys) => {
    if (!keys.length) return Promise.resolve();
    const [first, ...rest] = keys;
    return changeLineQuantityByKey(first, 0)
      .then((res) => {
        if (!res.ok) {
          return Promise.reject(
            new Error('Could not remove a bundle line from the cart.')
          );
        }
        return removeLineKeysSequentially(rest);
      });
  };

  /**
   * Drop every bundle component line for this parent (all groups), sequentially.
   */
  const clearAllBundleComponentsForParent = (cart, parentGid) => {
    if (!cart?.items?.length) return Promise.resolve();
    const keys = cart.items
      .filter((item) => isBundleComponentForParent(item, parentGid))
      .map((i) => i.key)
      .filter(Boolean);
    return removeLineKeysSequentially(keys);
  };

  /**
   * Drop every bundle line (components AND rewards) for this parent,
   * across all groups. Used by sections whose commit cycle owns both
   * the components and the tier reward lines, so a re-commit replaces
   * the whole bundle atomically (no stale rewards from a previous
   * tier hanging around).
   */
  const clearAllBundleLinesForParent = (cart, parentGid) => {
    if (!cart?.items?.length) return Promise.resolve();
    const keys = cart.items
      .filter((item) => isAnyBundleLineForParent(item, parentGid))
      .map((i) => i.key)
      .filter(Boolean);
    return removeLineKeysSequentially(keys);
  };

  /**
   * After the collapsed bundle parent line is removed from the cart,
   * tier gift lines (`_bxgy_source: bundle-set`) would otherwise remain.
   * New gift lines set `_bundle_set_parent_variant_id` to the parent
   * variant id so we can detect orphan gifts and remove them on any
   * cart-update when `findBundleParentLine` is missing.
   */
  const removeOrphanBundleSetGifts = (cart) => {
    if (!cart?.items?.length) return Promise.resolve(cart);
    const keysToRemove = [];
    for (const item of cart.items) {
      if (getProp(item, '_bxgy_source') !== 'bundle-set') continue;
      const pv = getProp(item, '_bundle_set_parent_variant_id');
      if (pv == null || String(pv).trim() === '') continue;
      const n = Number(pv);
      if (Number.isNaN(n)) continue;
      if (!findBundleParentLine(cart, n) && item.key) {
        keysToRemove.push(item.key);
      }
    }
    const unique = [...new Set(keysToRemove)];
    if (!unique.length) return Promise.resolve(cart);
    return removeLineKeysSequentially(unique)
      .then(() => fetchCart())
      .then((next) => {
        afterCartUpdate();
        return next;
      });
  };

  const initBundleSetOrphanGiftCleanup = () => {
    if (global.__bundleSetOrphanGiftCleanupInited) return;
    const pubsub = global.FoxTheme?.pubsub;
    if (!pubsub?.subscribe || !pubsub.PUB_SUB_EVENTS?.cartUpdate) return;
    global.__bundleSetOrphanGiftCleanupInited = true;
    let queue = Promise.resolve();
    pubsub.subscribe(pubsub.PUB_SUB_EVENTS.cartUpdate, (data) => {
      const nextCart = data?.cart;
      if (!nextCart?.items?.length) return;
      queue = queue
        .then(() => removeOrphanBundleSetGifts(nextCart))
        .catch(() => {});
    });
  };

  global.BundleCartHelpers = {
    getStoreRoot,
    parentVariantGid,
    formatMoney,
    fetchCart,
    productsJsonUrl,
    parseAddResponse,
    afterCartUpdate,
    changeLineQuantityByKey,
    getProp,
    isBundleComponentForParent,
    isBundleRewardForParent,
    isAnyBundleLineForParent,
    findBundleGroupIdsForParent,
    getCanonicalBundleGroupId,
    collectLinesForGroup,
    enforceSingleBundleGroup,
    clearAllBundleComponentsForParent,
    clearAllBundleLinesForParent,
    gidToVariantNumeric,
    parseBundleComponentVariantIds,
    parseIntArrayFromJsonProperty,
    hasBundledComponentMetadata,
    findBundleParentLine,
    clearBundleParentLineIfPresent,
    removeOrphanBundleSetGifts,
    initBundleSetOrphanGiftCleanup,
  };
})(typeof window !== 'undefined' ? window : this);

if (typeof window !== 'undefined' && window.document) {
  const boot = () => window.BundleCartHelpers?.initBundleSetOrphanGiftCleanup?.();
  if (window.document.readyState === 'loading') {
    window.document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
}

